import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import Logo from '@/components/Logo';
import { ArrowLeft } from 'lucide-react';
import SEO from '@/components/SEO';

export default function TermsOfService() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-[hsl(var(--background))]">
      <SEO
        title="Terms of Service"
        description="Terms of Service for Snap Windows — quote requests, general contracting services, payment terms, and legal information."
        path="/terms-of-service"
        noindex
      />
      {/* Nav */}
      <nav className="sticky top-0 z-50 bg-white/90 backdrop-blur border-b border-[hsl(var(--border))]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 flex items-center justify-between h-14">
          <Logo variant="light" size="small" />
          <Button variant="ghost" size="sm" onClick={() => navigate('/')} className="text-[var(--brand-slate-600)] text-xs">
            <ArrowLeft className="mr-1 h-4 w-4" />
            Back to Home
          </Button>
        </div>
      </nav>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-12">
        <h1 className="font-heading text-3xl sm:text-4xl font-semibold tracking-tight text-[var(--brand-slate-900)] mb-3">Terms of Service</h1>
        <p className="text-sm text-[var(--brand-slate-600)] mb-8">Last updated: {new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</p>

        <Card className="rounded-2xl shadow-[var(--shadow-soft)] mb-6">
          <CardContent className="p-6 sm:p-8 space-y-6">
            <section>
              <h2 className="font-heading text-xl font-semibold text-[var(--brand-slate-900)] mb-3">Agreement to Terms</h2>
              <p className="text-sm text-[var(--brand-slate-600)] leading-relaxed">
                By accessing and using snap-windows.com (the "Website") and our quote request services, you agree to be bound by these Terms of Service ("Terms"). If you do not agree to these Terms, please do not use our Website or services.
              </p>
            </section>

            <section>
              <h2 className="font-heading text-xl font-semibold text-[var(--brand-slate-900)] mb-3">About SNAP Windows</h2>
              <div className="bg-[var(--brand-ocean-50)] rounded-lg p-4 border border-[var(--brand-ocean-200)] text-sm text-[var(--brand-slate-600)]">
                <p className="leading-relaxed mb-3">
                  <strong>Company Name:</strong> SNAP Windows LLC
                </p>
                <p className="leading-relaxed mb-3">
                  <strong>Licensed Contractor:</strong> David Vasquez
                </p>
                <p className="leading-relaxed mb-3">
                  <strong>License Holder:</strong> HCC Group LLC
                </p>
                <p className="leading-relaxed">
                  SNAP Windows LLC operates using a licensed contractor. All work is performed under the qualifying license of David Vasquez through HCC Group LLC. We are fully insured with General Liability and Workers' Compensation coverage.
                </p>
              </div>
            </section>

            <section>
              <h2 className="font-heading text-xl font-semibold text-[var(--brand-slate-900)] mb-3">Quote Request Service</h2>
              <div className="space-y-3 text-sm text-[var(--brand-slate-600)]">
                <div>
                  <p className="font-semibold text-[var(--brand-slate-900)] mb-2">No-Obligation Estimates</p>
                  <p className="leading-relaxed">
                    Quote requests submitted through our Website are free and non-binding. A quote is an estimate based on the information and photos you provide. It is not a final contract or commitment from either party.
                  </p>
                </div>

                <div>
                  <p className="font-semibold text-[var(--brand-slate-900)] mb-2">Quote Accuracy</p>
                  <p className="leading-relaxed">
                    We strive to provide accurate estimates based on your photos and project details. However, final pricing may change after an in-person site visit, inspection, or discovery of unforeseen conditions (e.g., structural issues, code violations, or material availability).
                  </p>
                </div>

                <div>
                  <p className="font-semibold text-[var(--brand-slate-900)] mb-2">Quote Validity & Pricing</p>
                  <p className="leading-relaxed mb-2">
                    Quotes are valid for 14 days from the date issued.
                  </p>
                  <p className="leading-relaxed">
                    Due to manufacturer price fluctuations, pricing is subject to change. If a quote is accepted after a price increase has occurred, updated pricing may apply.
                  </p>
                </div>
              </div>
            </section>

            <section>
              <h2 className="font-heading text-xl font-semibold text-[var(--brand-slate-900)] mb-3">User Responsibilities</h2>
              <div className="space-y-2 text-sm text-[var(--brand-slate-600)]">
                <p className="leading-relaxed">When using our Website and services, you agree to:</p>
                <ul className="list-disc list-inside space-y-1 ml-4">
                  <li>Provide accurate, complete, and truthful information</li>
                  <li>Upload clear, recent photos of the windows and doors for which you are requesting a quote</li>
                  <li>Respond promptly to our communications regarding your quote</li>
                  <li>Not upload inappropriate, copyrighted, or unlawful content</li>
                  <li>Not use our services for any fraudulent or illegal purpose</li>
                </ul>
              </div>
            </section>

            <section>
              <h2 className="font-heading text-xl font-semibold text-[var(--brand-slate-900)] mb-3">General Contracting Services</h2>
              <div className="space-y-3 text-sm text-[var(--brand-slate-600)]">
                <div>
                  <p className="font-semibold text-[var(--brand-slate-900)] mb-2">Licensed & Insured</p>
                  <p className="leading-relaxed">
                    Snap Windows operates as a licensed General Contractor in the State of Florida (CGC1524851) and maintains General Liability and Workers' Compensation insurance for all projects.
                  </p>
                </div>

                <div>
                  <p className="font-semibold text-[var(--brand-slate-900)] mb-2">Permits and Inspections</p>
                  <p className="leading-relaxed">
                    All impact window and door installations require building permits in Florida. We handle the permitting process, including application, fees, and coordination with local building departments. Final inspections are required by law before project completion.
                  </p>
                </div>

                <div>
                  <p className="font-semibold text-[var(--brand-slate-900)] mb-2">Code Compliance</p>
                  <p className="leading-relaxed">
                    All installations are performed in accordance with the Florida Building Code (FBC) and High-Velocity Hurricane Zone (HVHZ) standards. We ensure compliance with local codes, manufacturer specifications, and industry best practices.
                  </p>
                </div>

                <div>
                  <p className="font-semibold text-[var(--brand-slate-900)] mb-2">HOA Requirements</p>
                  <p className="leading-relaxed">
                    If your property is governed by a Homeowners Association (HOA), you are responsible for obtaining HOA approval prior to installation. We can provide documentation and specifications to assist with your HOA application.
                  </p>
                </div>
              </div>
            </section>

            <section>
              <h2 className="font-heading text-xl font-semibold text-[var(--brand-slate-900)] mb-3">Limitation of Liability</h2>
              <div className="space-y-3 text-sm text-[var(--brand-slate-600)]">
                <div className="bg-amber-50 rounded-lg p-4 border border-amber-200">
                  <p className="font-semibold text-[var(--brand-slate-900)] mb-2">Important Disclaimer</p>
                  <p className="leading-relaxed">
                    TO THE MAXIMUM EXTENT PERMITTED BY LAW, SNAP WINDOWS SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES ARISING FROM YOUR USE OF OUR WEBSITE OR SERVICES.
                  </p>
                </div>

                <div>
                  <p className="leading-relaxed">Our liability is limited to:</p>
                  <ul className="list-disc list-inside space-y-1 ml-4">
                    <li>Website availability and functionality issues</li>
                    <li>Errors in quotes or estimates (correctable before contract signing)</li>
                    <li>Delays in quote preparation or communication</li>
                    <li>Third-party service failures (email, SMS, cloud storage)</li>
                  </ul>
                </div>

                <div>
                  <p className="leading-relaxed">
                    For contracted installation work, separate liability terms will be outlined in your signed construction agreement.
                  </p>
                </div>
              </div>
            </section>

            <section>
              <h2 className="font-heading text-xl font-semibold text-[var(--brand-slate-900)] mb-3">Payment Terms</h2>
              <div className="space-y-3 text-sm text-[var(--brand-slate-600)]">
                <p className="leading-relaxed">
                  Payment terms for contracted work are as follows:
                </p>
                <ul className="list-disc list-inside space-y-1 ml-4">
                  <li>50% of materials due at contract signing (plus permit processing fee)</li>
                  <li>50% of materials due prior to delivery</li>
                  <li>50% of installation cost due prior to installation</li>
                  <li>50% of installation cost due upon completion</li>
                </ul>
                
                <div className="mt-4">
                  <p className="font-semibold text-[var(--brand-slate-900)] mb-2">Payment Methods</p>
                  <p className="leading-relaxed mb-2">
                    Accepted payment methods include check, Zelle, and credit/debit cards.
                  </p>
                  <ul className="list-disc list-inside space-y-1 ml-4">
                    <li>Check and Zelle payments have no processing fee</li>
                    <li>Credit/debit card payments are subject to a 3.5% administrative fee</li>
                  </ul>
                </div>
              </div>
            </section>

            <section>
              <h2 className="font-heading text-xl font-semibold text-[var(--brand-slate-900)] mb-3">Intellectual Property</h2>
              <div className="space-y-2 text-sm text-[var(--brand-slate-600)]">
                <p className="leading-relaxed">
                  All content on this Website, including text, graphics, logos, and software, is the property of Snap Windows or its licensors and is protected by copyright and intellectual property laws.
                </p>
                <p className="leading-relaxed">
                  Photos you upload remain your property. By uploading photos, you grant us a limited license to use them for quote preparation and project management purposes only.
                </p>
              </div>
            </section>

            <section>
              <h2 className="font-heading text-xl font-semibold text-[var(--brand-slate-900)] mb-3">Third-Party Services</h2>
              <div className="space-y-2 text-sm text-[var(--brand-slate-600)]">
                <p className="leading-relaxed">
                  Our Website may utilize third-party services such as CRM systems, communication tools, payment processors, cloud storage, and analytics platforms.
                </p>
                <p className="leading-relaxed">
                  We are not responsible for the availability, accuracy, or policies of these third-party services.
                </p>
              </div>
            </section>

            <section>
              <h2 className="font-heading text-xl font-semibold text-[var(--brand-slate-900)] mb-3">Termination</h2>
              <p className="text-sm text-[var(--brand-slate-600)] leading-relaxed">
                We reserve the right to refuse service, suspend access, or terminate your use of our Website at our sole discretion for any reason, including violation of these Terms or fraudulent activity.
              </p>
            </section>

            <section>
              <h2 className="font-heading text-xl font-semibold text-[var(--brand-slate-900)] mb-3">Governing Law</h2>
              <p className="text-sm text-[var(--brand-slate-600)] leading-relaxed">
                These Terms are governed by the laws of the State of Florida, without regard to its conflict of law provisions. Any disputes arising from these Terms or our services shall be resolved in the courts of Florida.
              </p>
            </section>

            <section>
              <h2 className="font-heading text-xl font-semibold text-[var(--brand-slate-900)] mb-3">Changes to These Terms</h2>
              <p className="text-sm text-[var(--brand-slate-600)] leading-relaxed">
                We may update these Terms from time to time. Changes will be posted on this page with an updated "Last Updated" date. Your continued use of our Website after changes constitutes acceptance of the revised Terms.
              </p>
            </section>

            <section>
              <h2 className="font-heading text-xl font-semibold text-[var(--brand-slate-900)] mb-3">Severability</h2>
              <p className="text-sm text-[var(--brand-slate-600)] leading-relaxed">
                If any provision of these Terms is found to be invalid or unenforceable, the remaining provisions shall remain in full force and effect.
              </p>
            </section>

            <section>
              <h2 className="font-heading text-xl font-semibold text-[var(--brand-slate-900)] mb-3">Contact Information</h2>
              <div className="text-sm text-[var(--brand-slate-600)] space-y-1">
                <p className="leading-relaxed">If you have questions about these Terms of Service, please contact us:</p>
                <p className="font-semibold text-[var(--brand-slate-900)] mt-3">Snap Windows</p>
                <p>Licensed General Contractor: CGC1524851</p>
                <p>Email: <a href="mailto:quotes@snap-windows.com" className="text-[var(--brand-ocean-700)] hover:underline">quotes@snap-windows.com</a></p>
                <p>Serving: Miami-Dade, Broward, and Palm Beach Counties, Florida</p>
              </div>
            </section>
          </CardContent>
        </Card>

        <div className="text-center">
          <Button onClick={() => navigate('/')} className="bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] rounded-xl">
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Home
          </Button>
        </div>
      </div>
    </div>
  );
}
